/**
 * Environment Configuration Check Utility
 * Validates that all required environment variables are properly configured
 * and provides helpful error messages for missing or invalid configurations
 */

interface EnvironmentVariable {
  key: string;
  required: boolean;
  defaultValue?: string;
  validator?: (value: string) => boolean;
  description: string;
  example: string;
}

interface EnvironmentCheckResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  configuration: Record<string, string | undefined>;
}

/**
 * Define all environment variables used by the application
 */
const ENVIRONMENT_VARIABLES: EnvironmentVariable[] = [
  // API Configuration
  {
    key: 'VITE_API_URL',
    required: true,
    validator: (value) => value.startsWith('http'),
    description: 'Base URL for the API endpoint',
    example: 'https://api.mep-platform.com'
  },
  {
    key: 'VITE_API_TIMEOUT',
    required: false,
    defaultValue: '30000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'API request timeout in milliseconds',
    example: '30000'
  },

  // Feature Flags
  {
    key: 'VITE_ENABLE_MOCK_API',
    required: false,
    defaultValue: 'false',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Enable mock API for development',
    example: 'false'
  },
  {
    key: 'VITE_USE_REAL_API_USERS',
    required: false,
    defaultValue: 'true',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Use real API for user endpoints',
    example: 'true'
  },
  {
    key: 'VITE_ENABLE_MAINTENANCE_MODULE',
    required: false,
    defaultValue: 'true',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Enable maintenance module features',
    example: 'true'
  },
  {
    key: 'VITE_ENABLE_ANALYTICS',
    required: false,
    defaultValue: 'true',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Enable analytics tracking',
    example: 'true'
  },

  // Security Configuration
  {
    key: 'VITE_JWT_EXPIRY',
    required: false,
    defaultValue: '86400000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'JWT token expiry time in milliseconds',
    example: '86400000'
  },
  {
    key: 'VITE_SESSION_TIMEOUT',
    required: false,
    defaultValue: '1800000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'Session timeout in milliseconds',
    example: '1800000'
  },
  {
    key: 'VITE_MAX_LOGIN_ATTEMPTS',
    required: false,
    defaultValue: '5',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'Maximum login attempts before lockout',
    example: '5'
  },
  {
    key: 'VITE_LOCKOUT_DURATION',
    required: false,
    defaultValue: '900000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'Account lockout duration in milliseconds',
    example: '900000'
  },

  // Monitoring Configuration
  {
    key: 'VITE_ENABLE_ERROR_REPORTING',
    required: false,
    defaultValue: 'true',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Enable error reporting to monitoring service',
    example: 'true'
  },
  {
    key: 'VITE_ENABLE_PERFORMANCE_MONITORING',
    required: false,
    defaultValue: 'true',
    validator: (value) => ['true', 'false'].includes(value),
    description: 'Enable performance monitoring',
    example: 'true'
  },
  {
    key: 'VITE_LOG_LEVEL',
    required: false,
    defaultValue: 'error',
    validator: (value) => ['debug', 'info', 'warn', 'error', 'none'].includes(value),
    description: 'Logging level for the application',
    example: 'error'
  },
  // Application Settings
  {
    key: 'VITE_APP_NAME',
    required: false,
    defaultValue: 'MEP Platform',
    description: 'Application display name',
    example: 'MEP Platform'
  },
  {
    key: 'VITE_APP_VERSION',
    required: false,
    defaultValue: '1.0.0',
    validator: (value) => /^\d+\.\d+\.\d+/.test(value),
    description: 'Application version',
    example: '1.0.0'
  },
  {
    key: 'VITE_DEFAULT_LANGUAGE',
    required: false,
    defaultValue: 'pt',
    validator: (value) => ['pt', 'en', 'es'].includes(value),
    description: 'Default application language',
    example: 'pt'
  },
  {
    key: 'VITE_SUPPORTED_LANGUAGES',
    required: false,
    defaultValue: 'pt,en,es',
    validator: (value) => value.split(',').every(lang => ['pt', 'en', 'es'].includes(lang.trim())),
    description: 'Comma-separated list of supported languages',
    example: 'pt,en,es'
  },

  // Cache Configuration
  {
    key: 'VITE_CACHE_TTL',
    required: false,
    defaultValue: '3600000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'Cache time-to-live in milliseconds',
    example: '3600000'
  },
  {
    key: 'VITE_MAX_CACHE_SIZE',
    required: false,
    defaultValue: '100',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'Maximum number of cached items',
    example: '100'
  },

  // Rate Limiting
  {
    key: 'VITE_API_RATE_LIMIT',
    required: false,
    defaultValue: '100',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'API rate limit (requests per window)',
    example: '100'
  },
  {
    key: 'VITE_API_RATE_WINDOW',
    required: false,
    defaultValue: '60000',
    validator: (value) => !isNaN(parseInt(value)) && parseInt(value) > 0,
    description: 'API rate limit window in milliseconds',
    example: '60000'
  }
];

/**
 * Check if running in production environment
 */
export function isProduction(): boolean {
  return import.meta.env.MODE === 'production' || import.meta.env.PROD === true;
}

/**
 * Get environment variable value
 */
function getEnvVar(key: string): string | undefined {
  return import.meta.env[key];
}

/**
 * Validate all environment variables
 */
export function validateEnvironment(): EnvironmentCheckResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const configuration: Record<string, string | undefined> = {};

  ENVIRONMENT_VARIABLES.forEach((envVar) => {
    const value = getEnvVar(envVar.key);
    configuration[envVar.key] = value || envVar.defaultValue;

    // Check if required variable is missing
    if (envVar.required && !value) {
      errors.push(
        `Missing required environment variable: ${envVar.key}\n` +
        `  Description: ${envVar.description}\n` +
        `  Example: ${envVar.key}=${envVar.example}`
      );
      return;
    }

    // Use default value if not set
    const effectiveValue = value || envVar.defaultValue;

    // Validate the value if validator is provided
    if (effectiveValue && envVar.validator && !envVar.validator(effectiveValue)) {
      const message = 
        `Invalid value for environment variable: ${envVar.key}\n` +
        `  Current value: ${effectiveValue}\n` +
        `  Description: ${envVar.description}\n` +
        `  Example: ${envVar.key}=${envVar.example}`;
      
      if (envVar.required) {
        errors.push(message);
      } else {
        warnings.push(message);
      }
    }

    // Add warnings for missing optional variables in production
    if (!envVar.required && !value && isProduction()) {
      warnings.push(
        `Optional environment variable not set (using default): ${envVar.key}\n` +
        `  Default value: ${envVar.defaultValue}\n` +
        `  Description: ${envVar.description}`
      );
    }
  });

  // Additional production-specific checks
  if (isProduction()) {
    // Check for development values in production
    if (configuration['VITE_API_URL']?.includes('localhost')) {
      errors.push('VITE_API_URL contains localhost in production environment');
    }

    if (configuration['VITE_ENABLE_MOCK_API'] === 'true') {
      warnings.push('Mock API is enabled in production environment');
    }

    if (configuration['VITE_LOG_LEVEL'] === 'debug') {
      warnings.push('Debug logging is enabled in production environment');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    configuration
  };
}

/**
 * Log environment check results
 */
export function logEnvironmentCheck(result: EnvironmentCheckResult): void {
  const isProduction = import.meta.env.MODE === 'production';

  if (!result.valid) {
    console.error('❌ Environment Configuration Errors:');
    result.errors.forEach((error) => console.error(`\n${error}`));
  }

  if (result.warnings.length > 0 && !isProduction) {
    console.warn('⚠️  Environment Configuration Warnings:');
    result.warnings.forEach((warning) => console.warn(`\n${warning}`));
  }

  if (result.valid && result.warnings.length === 0) {
    console.info('✅ Environment configuration is valid');
  }

  // Log configuration summary in development
  if (!isProduction && result.valid) {
    console.info('\n📋 Environment Configuration Summary:');
    console.table(
      Object.entries(result.configuration).reduce((acc, [key, value]) => {
        const envVar = ENVIRONMENT_VARIABLES.find(v => v.key === key);
        acc[key] = {
          value: value?.includes('secret') || value?.includes('key') ? '***' : value,
          required: envVar?.required ? 'Yes' : 'No'
        };
        return acc;
      }, {} as Record<string, {
        value: string | undefined;
        required: string;
      }>)
    );
  }
}

/**
 * Create an environment configuration report
 */
export function generateEnvironmentReport(): string {
  const result = validateEnvironment();
  const timestamp = new Date().toISOString();
  
  let report = `# Environment Configuration Report\n`;
  report += `Generated: ${timestamp}\n`;
  report += `Environment: ${import.meta.env.MODE}\n\n`;

  if (result.errors.length > 0) {
    report += `## ❌ Errors (${result.errors.length})\n\n`;
    result.errors.forEach((error) => {
      report += `- ${error.replace(/\n/g, '\n  ')}\n\n`;
    });
  }

  if (result.warnings.length > 0) {
    report += `## ⚠️  Warnings (${result.warnings.length})\n\n`;
    result.warnings.forEach((warning) => {
      report += `- ${warning.replace(/\n/g, '\n  ')}\n\n`;
    });
  }

  report += `## 📋 Configuration\n\n`;
  report += '| Variable | Value | Required | Status |\n';
  report += '|----------|-------|----------|--------|\n';
  
  ENVIRONMENT_VARIABLES.forEach((envVar) => {
    const value = result.configuration[envVar.key];
    const displayValue = value?.includes('secret') || value?.includes('key') ? '***' : value || 'Not set';
    const isValid = !envVar.required || !!value;
    const status = isValid ? '✅' : '❌';
    
    report += `| ${envVar.key} | ${displayValue} | ${envVar.required ? 'Yes' : 'No'} | ${status} |\n`;
  });

  return report;
}

/**
 * Check for critical environment issues that should prevent app startup
 */
export function checkCriticalEnvironment(): void {
  const result = validateEnvironment();
  
  if (!result.valid) {
    const errorMessage = 
      'Critical environment configuration errors detected:\n\n' +
      result.errors.join('\n\n') +
      '\n\nPlease fix these errors before starting the application.';
    
    if (isProduction()) {
      // In production, throw error to prevent startup
      throw new Error(errorMessage);
    } else {
      // In development, log error but allow startup
      console.error(errorMessage);
    }
  }
}

/**
 * Get safe environment configuration for client-side use
 * Excludes sensitive values and provides only necessary config
 */
export function getSafeEnvironmentConfig(): Record<string, string | undefined> {
  const result = validateEnvironment();
  const safeConfig: Record<string, string | undefined> = {};

  // Define which variables are safe to expose
  const safeKeys = [
    'VITE_APP_NAME',
    'VITE_APP_VERSION',
    'VITE_DEFAULT_LANGUAGE',
    'VITE_SUPPORTED_LANGUAGES',
    'VITE_ENABLE_ANALYTICS',
    'VITE_LOG_LEVEL'
  ];

  safeKeys.forEach((key) => {
    if (result.configuration[key]) {
      safeConfig[key.replace('VITE_', '')] = result.configuration[key];
    }
  });

  return safeConfig;
}